import type {
  ICountry,
  IDistrict,
  IProvince,
  ISubDistrict,
} from 'features/Address/model'
import type { IIndexable } from 'helpers/model'
import type { Dispatch } from 'react'
import type { Address, CompanyForFormSubmit } from 'types'

import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useRecoilValue } from 'recoil'

import { getAddressField } from 'helpers/common'
import * as utils from 'helpers/getAddress'
import { companyEditAddressState } from 'recoils/atoms/companyEditAtom'

type UseCompanyAddressEdit = (
  countries: ICountry[],
  setProvinces: Dispatch<IProvince[]>,
  setDistricts: Dispatch<IDistrict[]>,
  setSubDistricts: Dispatch<ISubDistrict[]>,
  setIsProvincesLoading: Dispatch<boolean>,
  setIsDistrictsLoading: Dispatch<boolean>,
  setIsSubDistrictsLoading: Dispatch<boolean>,
) => void

export const useCompanyAddressEdit: UseCompanyAddressEdit = (
  countries,
  setProvinces,
  setDistricts,
  setSubDistricts,
  setIsProvincesLoading,
  setIsDistrictsLoading,
  setIsSubDistrictsLoading,
) => {
  const { setValue } = useFormContext<CompanyForFormSubmit>()
  const address: Address & IIndexable = useRecoilValue(companyEditAddressState)

  const setAddress = async () => {
    setIsProvincesLoading(true)
    setIsDistrictsLoading(true)
    setIsSubDistrictsLoading(true)

    const country = getAddressField(countries, 'country', address)
    setValue('country', country)

    const { provinces } = await utils.getProvinces(country.id)
    const province = getAddressField(provinces, 'province', address)
    setValue('province', province)
    setProvinces(provinces)
    setIsProvincesLoading(false)

    const { districts } = await utils.getDistricts(province.id)
    const district = getAddressField(districts, 'district', address)
    setValue('district', district)
    setDistricts(districts)
    setIsDistrictsLoading(false)

    const { subDistricts } = await utils.getSubDistricts(district.id)
    const subDistrict = getAddressField(subDistricts, 'subDistrict', address)
    setValue('subDistrict', subDistrict)
    setSubDistricts(subDistricts)
    setIsSubDistrictsLoading(false)
  }

  useEffect(() => {
    if (isEmpty(address)) {
      return
    }

    setAddress()
  }, [address, countries])
}
